import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { toast } from "sonner";
import { useTRPC } from "@/trpc";

const CONFLICT_CODE = "CONFLICT";

/**
 * Shared success and error handling for wage set writes. A CONFLICT means
 * the screen was stale (or the name or assignment is taken), so the message
 * is shown and the wage set data is refetched.
 */
export function useOrganizationWageSetMutationHandlers() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const invalidateOrganizationWageSets = useCallback(async () => {
    await queryClient.invalidateQueries(trpc.organizationWageSet.pathFilter());
  }, [queryClient, trpc]);

  const handleError = useCallback(
    (error: unknown) => {
      if (error instanceof TRPCClientError) {
        toast.error(error.message);
        if (error.data?.code === CONFLICT_CODE) {
          void invalidateOrganizationWageSets();
        }
        return;
      }
      toast.error("Something went wrong. Please try again.");
    },
    [invalidateOrganizationWageSets],
  );

  return { invalidateOrganizationWageSets, handleError };
}
